import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConstrainMode, DetailsListLayoutMode } from "@fluentui/react/lib/DetailsList";
import { MarqueeSelection } from "@fluentui/react/lib/MarqueeSelection";
import { ScrollablePane, ScrollbarVisibility } from "@fluentui/react/lib/ScrollablePane";
import { ShimmeredDetailsList } from "@fluentui/react/lib/ShimmeredDetailsList";

import { WithTranslator } from "Components/Generic/Translator";
import { IProject } from "Model/Storage/IProject";
import { uiSetActiveProjectId, uiSetDetailsOpen } from "Store/Actions";
import { hasNoElements } from "Utils/Array";

import { selectIsLoading } from "Store/Selectors/UI";
import { useGridStyles } from "./UseGridStyles";
import { onRenderDetailsHeader } from "./Header";
import { useProjects, useSelection, useSortedColumns } from "./Hooks";
import { IGridProps } from "./IGridProps";

export const Grid = WithTranslator(({ translator }: IGridProps): JSX.Element => {
  const dispatch = useDispatch();
  const { onSort, columnList } = useSortedColumns();
  const { projectList, projectListHash } = useProjects();
  const selection = useSelection();
  const isLoading: boolean = useSelector(selectIsLoading);
  const styles = useGridStyles();

  const onItemInvoked = useCallback(
    (activeProject: IProject) => {
      dispatch(uiSetActiveProjectId(activeProject[`@id`]));
      dispatch(uiSetDetailsOpen(true));
    },
    [dispatch]
  );

  if (hasNoElements(projectList) && !isLoading) {
    return <div className={styles.noprojects}>{translator.get(`overview.noprojects`, `No projects yet.`)}</div>;
  }

  return (
    <div className={styles.root}>
      <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
        <MarqueeSelection selection={selection}>
          <ShimmeredDetailsList
            columns={columnList}
            constrainMode={ConstrainMode.unconstrained}
            enableShimmer={hasNoElements(projectList)}
            enableUpdateAnimations
            items={projectList}
            layoutMode={DetailsListLayoutMode.fixedColumns}
            onColumnHeaderClick={onSort}
            onItemInvoked={onItemInvoked}
            onRenderDetailsHeader={onRenderDetailsHeader}
            removeFadingOverlay
            selection={selection}
            selectionPreservedOnEmptyClick
            setKey={projectListHash}
            useFastIcons
            usePageCache
            // useReducedRowRenderer
          />
        </MarqueeSelection>
      </ScrollablePane>
    </div>
  );
});
